/**
 * Application Configuration
 *
 * SETUP INSTRUCTIONS:
 * Required configuration is loaded from environment variables at build
 * time (Vite) or process startup (Node). See `Visualizer/.env.example`
 * for the full list of required `VITE_*` vars and the matching
 * provider-dashboard sources, and `Visualizer/SETUP.md` for the full
 * walkthrough.
 *
 * Quick start:
 *   1. cp Visualizer/.env.example Visualizer/.env.local
 *   2. Fill in real values from your Supabase / Stripe / PayPal dashboards.
 *   3. Restart `npm run dev` so Vite picks up the new env vars.
 *
 * If any required variable is missing, this module throws at load time
 * (via `requireEnv`) — that's intentional. We fail fast on bad config
 * rather than silently embedding `undefined` into payment URLs at
 * runtime.
 *
 * TIER CONFIGURATION:
 * Tier features and pricing are managed in the database and fetched
 * dynamically.
 * - Use tierConfigService.getTierConfig() for tier definitions and pricing
 * - Use entitlementService.getEntitlements() for the current user's tier
 * - See SETUP.md for database schema and tier configuration
 */

import { requireEnv } from './env.js';

// Supabase Configuration
//
// These are the ONLY env vars required by `config.js` itself. Payment
// provider keys (Stripe, PayPal) and Stripe Price IDs are consumed
// directly by their respective service modules (`checkout-service.js`,
// `paypal-service.js`) — they are NOT exported from here.
//
// This split matters for non-payment consumers: Admin Node scripts and
// any other Supabase-only importer should be able to load `config.js`
// without needing Stripe/PayPal vars in their shell. Putting payment
// requireEnv() calls here would break those callers at import time.
export const SUPABASE_URL = requireEnv('VITE_SUPABASE_URL');
export const SUPABASE_ANON_KEY = requireEnv('VITE_SUPABASE_ANON_KEY');

// Analytics Configuration (same accounts as ShowLark landing)
// Intentionally hardcoded for now — moving these to env vars is a
// separate follow-up. They are public IDs (already exposed in shipped
// client code) so the urgency is lower than for the values above.
export const GA4_MEASUREMENT_ID = 'G-4ZJGYKY2L4';
export const POSTHOG_API_KEY = 'phc_OIaFRoSRBh1xazg0yUwz9cYwi8dYPxKzDiQxAwPO6Ju';
export const POSTHOG_API_HOST = 'https://dkeiv83n19cfke.showlark.com/';

// Development environment detection
export const DEV_HOSTNAMES = ['localhost', '127.0.0.1'];
export const DEV_PORTS = ['5173', '3000', '3001', '8080'];

export function isDevelopmentHost(hostname = (typeof window !== 'undefined' ? window.location.hostname : '')) {
  return DEV_HOSTNAMES.includes(hostname);
}

export function isDevelopmentPort(port = (typeof window !== 'undefined' ? window.location.port : '')) {
  return DEV_PORTS.includes(port);
}

export function isDevelopmentEnvironment() {
  return import.meta.env?.DEV || isDevelopmentHost();
}

/**
 * Configuration validity check.
 *
 * Since `requireEnv` above throws at module load if any required var
 * is missing, this function always returns `true` when the module
 * successfully loads. It's kept for compatibility with existing
 * callers (auth-service, entitlement-service, visualizer-loader,
 * thumbnail-service, custom-visualizer, auth-ui) that previously used
 * it as a "should I even attempt this Supabase call?" gate. They no
 * longer need to short-circuit — config is guaranteed to be set.
 */
export function isConfigured() {
  return true;
}
